import { Search } from "@mui/icons-material";
import { Box, Button, IconButton, InputAdornment } from "@mui/material";
import { Formik, Form } from "formik";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import Table, { TableColumns } from "~frontend/components/Table";
import TagChip from "~frontend/components/TagChip";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { ListTagsWithIntegrationsTag } from "~frontend/graphql/tags";
import { palette } from "~frontend/theme";

export default function GlobalTags({
  tags,
  onAddTags,
  integrationTableColumns,
}: {
  tags: ListTagsWithIntegrationsTag[];
  onAddTags: (tagIds: string[]) => Promise<void>;
  integrationTableColumns: TableColumns<ListTagsWithIntegrationsTag>;
}): ReactElement {
  const [search, setSearch] = useState("");

  const filteredTags = useMemo(
    () =>
      tags.filter(tag => tag.name.toLowerCase().includes(search.toLowerCase())),
    [tags, search]
  );

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [addingTags, setAddingTags] = useState(false);

  useEffect(() => {
    const filteredTagIds = new Set(filteredTags.map(tag => tag.id));
    const filteredSelectedTagIds = selectedTagIds.filter(id =>
      filteredTagIds.has(id)
    );
    if (filteredSelectedTagIds.length !== selectedTagIds.length) {
      setSelectedTagIds(filteredSelectedTagIds);
    }
  }, [filteredTags, selectedTagIds]);

  const handleAddTags = useCallback(
    async (tagIds: string[]) => {
      setAddingTags(true);
      try {
        await onAddTags(tagIds);
        setSelectedTagIds([]);
      } finally {
        setAddingTags(false);
      }
    },
    [setAddingTags, onAddTags, setSelectedTagIds]
  );

  return (
    <>
      <Formik
        initialValues={{ search }}
        validationSchema={Yup.object().shape({
          search: Yup.string(),
        })}
        onSubmit={values => setSearch(values.search)}
      >
        {({ submitForm }) => (
          <Form style={{ flex: 1 }}>
            {/* TODO: enter to submit */}
            <FormikTextField
              fullWidth
              name="search"
              onBlur={submitForm}
              placeholder="Search for a tag"
              sx={{ pt: 0, mb: 1 }}
              InputProps={{
                sx: { fontSize: "12px", color: palette.darkGray },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={submitForm}>
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Form>
        )}
      </Formik>
      <Box sx={{ height: "300px", overflow: "scroll", pb: 3 }}>
        <Table
          size="small"
          columns={[
            {
              key: "name",
              title: "Tag Name",
              renderValue: tag => <TagChip label={tag.name} />,
            },
            ...integrationTableColumns,
          ]}
          rows={filteredTags}
          noFooter
          selectedRows={selectedTagIds}
          onChangeSelectedRows={setSelectedTagIds}
          selectDisabled={addingTags}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
        columnGap={1}
      >
        <Button
          variant="contained"
          color="tertiary"
          sx={{
            backgroundColor: palette.midGray,
            "&:hover": {
              backgroundColor: palette.darkGray,
              color: palette.white,
            },
          }}
          disabled={addingTags || tags.length === 0}
          onClick={() => handleAddTags(tags.map(tag => tag.id))}
        >
          Import All Tags
        </Button>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          sx={
            addingTags || selectedTagIds.length === 0
              ? {
                  backgroundColor: `${palette.lightGray} !important`,
                  border: `1px solid ${palette.midGray} !important`,
                  color: palette.navy,
                }
              : {}
          }
          disabled={addingTags || selectedTagIds.length === 0}
          onClick={() => handleAddTags(selectedTagIds)}
        >
          Import Selected
        </Button>
      </Box>
    </>
  );
}
